import { ArchiveError, OMFZ_EXTENSION } from '../types/driveArchive';

/**
 * .omfz に書き添えるアプリ専用メタ (不可視 key-value)。過去分は欠落する。
 * issue #169: 履歴一覧の「フルネーム / 総譜・パート譜 / パート」表示に使う title/mode/part。
 * issue #177: pdfFileId は完成 PDF の fileId。 復元時に fileId 直取りでリンクを引くため。
 * DriveOmfzFile と listOmfzFiles の生レスポンス型で共有する (単一ソース化)。
 */
export interface DriveOmfzAppProperties {
  title?: string;
  mode?: string;
  part?: string;
  pdfFileId?: string;
}

export interface DriveOmfzFile {
  id: string;
  name: string;
  modifiedTime: string;
  size: number;
  description?: string;
  appProperties?: DriveOmfzAppProperties;
}

/** 既定の保存先フォルダ名。Drive 上の実フォルダ名と UI の保存先ラベルの単一ソース。 */
export const APP_FOLDER_NAME = 'おまかせ運指くん';

/** issue #103: .omfz を隠すサブフォルダ名。backend drive_uploader.py ARCHIVE_SUBFOLDER_NAME と一致。 */
export const ARCHIVE_SUBFOLDER_NAME = '運指アーカイブ';

const DRIVE_API = 'https://www.googleapis.com/drive/v3';
const DRIVE_UPLOAD = 'https://www.googleapis.com/upload/drive/v3';

type FetchImpl = typeof fetch;

export interface DriveAuth {
  token: string;
  fetchImpl?: FetchImpl;
  /** issue #35: 401 時にトークンを更新して 1 回だけリトライするための取得関数。未注入なら従来通り即エラー。 */
  refreshToken?: () => Promise<string>;
}

/** RequestInit の Authorization ヘッダを指定トークンで差し替えた新 init を返す。 */
function withAuth(init: RequestInit, token: string): RequestInit {
  return {
    ...init,
    headers: { ...(init.headers as Record<string, string>), Authorization: `Bearer ${token}` },
  };
}

/** 401 → ArchiveError 変換以外のステータス検査を共通化する。 */
function throwForStatus(res: Response, body: string): never {
  if (res.status === 401) throw new ArchiveError('not_authorized', 'Drive access token rejected');
  if (res.status === 403) throw new ArchiveError('quota_exceeded', 'Drive API forbidden/quota');
  throw new ArchiveError('network', `Drive API ${res.status} ${res.statusText}: ${body}`);
}

async function driveFetch(
  url: string,
  init: RequestInit,
  auth: DriveAuth,
): Promise<Response> {
  const fetchImpl = auth.fetchImpl ?? fetch;
  let res = await fetchImpl(url, init);
  // issue #35 M-1: 401 かつ refreshToken があれば、新トークンで 1 回だけリトライする (無限ループ防止)。
  if (res.status === 401 && auth.refreshToken) {
    const fresh = await auth.refreshToken();
    res = await fetchImpl(url, withAuth(init, fresh));
  }
  if (res.ok) return res;
  const body = await res.text().catch(() => '');
  throwForStatus(res, body);
}

/**
 * 公開共有 Drive ファイル (「リンクを知る全員が閲覧可」) を API キーのみで直接ダウンロードする (issue #128)。
 * OAuth scope を一切使わない (Authorization ヘッダ無し) ため restricted scope を要求せず CASA 対象外。
 * 非公開/権限外ファイルは 403/404 を返すので、呼び出し側で drive.file Picker へフォールバックさせる。
 * CORS/network で fetch が reject した場合も throw を伝播する (フォールバック判定に使う)。
 */
export async function downloadPublicDriveFile(
  fileId: string,
  apiKey: string,
  fetchImpl: FetchImpl = fetch,
): Promise<Blob> {
  const url = `${DRIVE_API}/files/${fileId}?alt=media&key=${encodeURIComponent(apiKey)}`;
  const res = await fetchImpl(url);
  if (!res.ok) {
    throw new ArchiveError('network', `public download failed: ${res.status}`);
  }
  return res.blob();
}

export async function ensureAppFolder(auth: DriveAuth): Promise<string> {
  const q = encodeURIComponent(
    `name = '${APP_FOLDER_NAME.replace(/'/g, "\\'")}' and mimeType = 'application/vnd.google-apps.folder' and trashed = false`,
  );
  const listRes = await driveFetch(
    `${DRIVE_API}/files?q=${q}&fields=files(id,name)&spaces=drive`,
    { headers: { Authorization: `Bearer ${auth.token}` } },
    auth,
  );
  const list = (await listRes.json()) as { files?: Array<{ id: string; name: string }> };
  if (list.files && list.files.length > 0) return list.files[0].id;

  const createRes = await driveFetch(
    `${DRIVE_API}/files`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${auth.token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        name: APP_FOLDER_NAME,
        mimeType: 'application/vnd.google-apps.folder',
      }),
    },
    auth,
  );
  const created = (await createRes.json()) as { id: string };
  return created.id;
}

/**
 * issue #103: `parentFolderId` 直下に `name` のフォルダを find-or-create し id を返す。
 * `.omfz` をサブフォルダへ隔離する用途。`ensureAppFolder` の親指定版。
 */
export async function ensureSubFolder(
  auth: DriveAuth,
  parentFolderId: string,
  name: string,
): Promise<string> {
  const q = encodeURIComponent(
    `name = '${name.replace(/'/g, "\\'")}' and mimeType = 'application/vnd.google-apps.folder' and '${parentFolderId}' in parents and trashed = false`,
  );
  const listRes = await driveFetch(
    `${DRIVE_API}/files?q=${q}&fields=files(id,name)&spaces=drive`,
    { headers: { Authorization: `Bearer ${auth.token}` } },
    auth,
  );
  const list = (await listRes.json()) as { files?: Array<{ id: string; name: string }> };
  if (list.files && list.files.length > 0) return list.files[0].id;

  const createRes = await driveFetch(
    `${DRIVE_API}/files`,
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${auth.token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name,
        mimeType: 'application/vnd.google-apps.folder',
        parents: [parentFolderId],
      }),
    },
    auth,
  );
  const created = (await createRes.json()) as { id: string };
  return created.id;
}

export interface UploadMultipartInput extends DriveAuth {
  folderId: string;
  fileName: string;
  description: string;
  blob: Blob;
}

export async function uploadArchiveMultipart(input: UploadMultipartInput): Promise<string> {
  const boundary = `omfz_${Math.random().toString(16).slice(2)}`;
  const metadata = {
    name: input.fileName,
    parents: [input.folderId],
    description: input.description,
    mimeType: 'application/zip',
  };
  const preamble = new Blob(
    [
      `--${boundary}\r\n`,
      'Content-Type: application/json; charset=UTF-8\r\n\r\n',
      JSON.stringify(metadata),
      `\r\n--${boundary}\r\n`,
      'Content-Type: application/zip\r\n\r\n',
    ],
    { type: 'text/plain' },
  );
  const closing = new Blob([`\r\n--${boundary}--`], { type: 'text/plain' });
  const body = new Blob([preamble, input.blob, closing], {
    type: `multipart/related; boundary=${boundary}`,
  });
  const res = await driveFetch(
    `${DRIVE_UPLOAD}/files?uploadType=multipart&fields=id`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${input.token}`,
        'Content-Type': `multipart/related; boundary=${boundary}`,
      },
      body,
    },
    input,
  );
  const json = (await res.json()) as { id: string };
  return json.id;
}

export interface UploadPdfInput extends DriveAuth {
  folderId: string;
  fileName: string;
  blob: Blob;
}

/**
 * issue #103: 注釈済み PDF 単体を `folderId` 直下へ upsert upload し file_id を返す。
 * 同名が既にあれば中身を PATCH media で上書き (fileId・名前据え置き)、無ければ multipart create。
 * fileName は呼び出し側がフロント DL 名 (`stored.fileName`) をそのまま渡す。
 */
export async function uploadPdfFile(input: UploadPdfInput): Promise<string> {
  // issue #103: 同名が既にあれば中身を上書き (fileId・名前据え置き)、無ければ新規作成。
  const existingId = await findFileIdByName(input, input.folderId, input.fileName);
  if (existingId) {
    const res = await driveFetch(
      `${DRIVE_UPLOAD}/files/${existingId}?uploadType=media&fields=id`,
      {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${input.token}`, 'Content-Type': 'application/pdf' },
        body: input.blob,
      },
      input,
    );
    const json = (await res.json()) as { id: string };
    return json.id;
  }

  const boundary = `pdf_${Math.random().toString(16).slice(2)}`;
  const metadata = {
    name: input.fileName,
    parents: [input.folderId],
    mimeType: 'application/pdf',
  };
  const preamble = new Blob(
    [
      `--${boundary}\r\n`,
      'Content-Type: application/json; charset=UTF-8\r\n\r\n',
      JSON.stringify(metadata),
      `\r\n--${boundary}\r\n`,
      'Content-Type: application/pdf\r\n\r\n',
    ],
    { type: 'text/plain' },
  );
  const closing = new Blob([`\r\n--${boundary}--`], { type: 'text/plain' });
  const body = new Blob([preamble, input.blob, closing], {
    type: `multipart/related; boundary=${boundary}`,
  });
  const res = await driveFetch(
    `${DRIVE_UPLOAD}/files?uploadType=multipart&fields=id`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${input.token}`,
        'Content-Type': `multipart/related; boundary=${boundary}`,
      },
      body,
    },
    input,
  );
  const json = (await res.json()) as { id: string };
  return json.id;
}

const RESUMABLE_INIT = `${DRIVE_UPLOAD}/files?uploadType=resumable&fields=id`;

export interface UploadResumableInput extends DriveAuth {
  folderId: string;
  fileName: string;
  description: string;
  blob: Blob;
  chunkSize?: number;
  onProgress?: (sentBytes: number, totalBytes: number) => void;
  refreshToken?: () => Promise<string>;
  /** issue #169: アプリ専用メタ (title/mode/part)。指定時のみ init body の appProperties に載せる。 */
  appProperties?: Record<string, string>;
}

export async function uploadArchiveResumable(input: UploadResumableInput): Promise<string> {
  const fetchImpl = input.fetchImpl ?? fetch;
  const chunkSize = Math.max(input.chunkSize ?? 1024 * 1024, 256 * 1024);
  const total = input.blob.size;
  let token = input.token;

  const initRes = await fetchImpl(RESUMABLE_INIT, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json; charset=UTF-8',
      'X-Upload-Content-Type': 'application/zip',
      'X-Upload-Content-Length': String(total),
    },
    body: JSON.stringify({
      name: input.fileName,
      parents: [input.folderId],
      description: input.description,
      mimeType: 'application/zip',
      ...(input.appProperties ? { appProperties: input.appProperties } : {}),
    }),
  });
  if (!initRes.ok) {
    const body = await initRes.text().catch(() => '');
    throw new ArchiveError('network', `Resumable init failed: ${initRes.status} ${body}`);
  }
  const sessionUrl = initRes.headers.get('Location');
  if (!sessionUrl) throw new ArchiveError('network', 'Resumable session URL missing');

  let offset = 0;
  while (offset < total) {
    const end = Math.min(offset + chunkSize, total);
    const chunk = input.blob.slice(offset, end);
    const res = await fetchImpl(sessionUrl, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Length': String(end - offset),
        'Content-Range': `bytes ${offset}-${end - 1}/${total}`,
      },
      body: chunk,
    });
    if (res.status === 200 || res.status === 201) {
      const json = (await res.json()) as { id: string };
      input.onProgress?.(total, total);
      return json.id;
    }
    if (res.status === 308) {
      const range = res.headers.get('Range');
      const match = range?.match(/bytes=0-(\d+)/);
      offset = match ? Number(match[1]) + 1 : end;
      input.onProgress?.(offset, total);
      continue;
    }
    if (res.status === 401 && input.refreshToken) {
      token = await input.refreshToken();
      const statusRes = await fetchImpl(sessionUrl, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Length': '0',
          'Content-Range': `bytes */${total}`,
        },
      });
      if (statusRes.status === 308) {
        const range = statusRes.headers.get('Range');
        const match = range?.match(/bytes=0-(\d+)/);
        offset = match ? Number(match[1]) + 1 : 0;
        continue;
      }
      if (statusRes.status === 200 || statusRes.status === 201) {
        const json = (await statusRes.json()) as { id: string };
        return json.id;
      }
      throw new ArchiveError('not_authorized', `Token refresh status query failed: ${statusRes.status}`);
    }
    const body = await res.text().catch(() => '');
    throw new ArchiveError('network', `Resumable PUT failed: ${res.status} ${body}`);
  }
  throw new ArchiveError('unknown', 'Resumable upload exited loop without success');
}

/** folderId 直下・同名・非trashed に絞る files.list の q パラメータ (encodeURIComponent 済み)。 */
function namedChildFileQuery(folderId: string, fileName: string): string {
  return encodeURIComponent(
    `name = '${fileName.replace(/'/g, "\\'")}' and '${folderId}' in parents and trashed = false`,
  );
}

/** files.list を driveFetch (401 リトライ内蔵) で叩き files 配列を返す。 ヒット無しは []。 */
async function listFiles<T>(auth: DriveAuth, url: string): Promise<T[]> {
  const res = await driveFetch(url, { headers: { Authorization: `Bearer ${auth.token}` } }, auth);
  const body = (await res.json()) as { files?: T[] };
  return body.files ?? [];
}

/**
 * issue #103: parentFolderId 直下の同名・非trashed ファイルを modifiedTime 降順で探し、
 * 最新の file id を返す。無ければ null。
 */
export async function findFileIdByName(
  auth: DriveAuth,
  folderId: string,
  fileName: string,
): Promise<string | null> {
  const q = namedChildFileQuery(folderId, fileName);
  const url = `${DRIVE_API}/files?q=${q}&fields=files(id)&orderBy=modifiedTime%20desc&pageSize=1&spaces=drive`;
  const files = await listFiles<{ id: string }>(auth, url);
  return files[0]?.id ?? null;
}

/**
 * issue #152: folderId 直下の同名・非trashed .omfz を全件探し id 配列を返す (重複除去用)。
 * resumable upload は常に新規ファイルを作るため、 同一結果を再保存すると .omfz が重複する。
 * 保存前に本関数で既存同名を検索→ deleteOmfzFile で消してから upload することで upsert 化する。
 * findFileIdByName と違い pageSize=1 に絞らず全件返す (過去に溜まった重複もまとめて掃除する)。
 */
export async function findOmfzByName(
  auth: DriveAuth,
  folderId: string,
  fileName: string,
): Promise<string[]> {
  const q = namedChildFileQuery(folderId, fileName);
  const url = `${DRIVE_API}/files?q=${q}&fields=files(id)&pageSize=100&spaces=drive`;
  const files = await listFiles<{ id: string }>(auth, url);
  return files.map((f) => f.id);
}

/**
 * issue #140: folderId 直下の同名・非trashed PDF を modifiedTime 降順で探し、 最新ファイルの
 * 共有リンク (webViewLink) を返す。 findFileIdByName と同じ q/orderBy/pageSize 契約。
 * webViewLink 欠落時は id から /view URL を組み立てる。 ヒット無しは null。
 * Drive の共有設定・権限は一切変更しない (URL を読むだけ)。
 */
export async function findPdfWebViewLink(
  auth: DriveAuth,
  folderId: string,
  fileName: string,
): Promise<string | null> {
  const q = namedChildFileQuery(folderId, fileName);
  const url = `${DRIVE_API}/files?q=${q}&fields=files(id,webViewLink)&orderBy=modifiedTime%20desc&pageSize=1&spaces=drive`;
  const files = await listFiles<{ id: string; webViewLink?: string }>(auth, url);
  const file = files[0];
  if (!file) return null;
  return file.webViewLink ?? `https://drive.google.com/file/d/${file.id}/view`;
}

/**
 * .omfz ファイル一覧を modifiedTime 降順で取得 (issue #24 Phase 1 / issue #71 横断検索化)。
 *
 * issue #71: parents 指定を廃止し全 .omfz を横断検索する。 scope 'drive.file' で
 * 自アプリ作成分のみ可視となるため他アプリのファイルは混入しない (Drive API 仕様)。
 * これにより手動保存先 (フッタ指定フォルダ) / 自動保存先 (固定フォルダ) の双方が一覧に乗る。
 */
export async function listOmfzFiles(
  auth: DriveAuth,
): Promise<DriveOmfzFile[]> {
  const q = encodeURIComponent(
    `name contains '${OMFZ_EXTENSION}' and trashed = false`,
  );
  const fields = encodeURIComponent('files(id,name,modifiedTime,size,description,appProperties)');
  const url = `${DRIVE_API}/files?q=${q}&fields=${fields}&orderBy=modifiedTime%20desc&pageSize=100&spaces=drive`;
  const res = await driveFetch(
    url,
    { headers: { Authorization: `Bearer ${auth.token}` } },
    auth,
  );
  const body = (await res.json()) as {
    files?: Array<{
      id: string;
      name: string;
      modifiedTime: string;
      size?: string;
      description?: string;
      appProperties?: DriveOmfzAppProperties;
    }>;
  };
  return (body.files ?? []).map((f) => ({
    id: f.id,
    name: f.name,
    modifiedTime: f.modifiedTime,
    size: f.size ? parseInt(f.size, 10) : 0,
    description: f.description,
    appProperties: f.appProperties,
  }));
}

/**
 * issue #138: 指定 fileId の .omfz を Drive から削除する。 driveFetch 経由 (401 リトライ内蔵)。
 * 成功時 (200/204) は void。 throwForStatus で 401→not_authorized / 403→quota_exceeded /
 * その他→network に変換する。 元 PDF は別ファイルなので残る (.omfz のみ削除)。
 */
export async function deleteOmfzFile(auth: DriveAuth, fileId: string): Promise<void> {
  await driveFetch(
    `${DRIVE_API}/files/${fileId}`,
    { method: 'DELETE', headers: { Authorization: `Bearer ${auth.token}` } },
    auth,
  );
}

/**
 * 指定 fileId の .omfz Blob を取得 (issue #24 Phase 1)。
 * spec §4.5
 */
export async function downloadOmfzBlob(
  auth: DriveAuth,
  fileId: string,
): Promise<Blob> {
  const res = await driveFetch(
    `${DRIVE_API}/files/${fileId}?alt=media`,
    { headers: { Authorization: `Bearer ${auth.token}` } },
    auth,
  );
  return res.blob();
}

/**
 * issue #177: fileId 直接指定で完成 PDF の共有リンク (webViewLink) を取得する。
 * omfz appProperties/保存時に控えた pdfFileId から引くため、 フォルダ移動/名前変更に強い
 * (単一フォルダ探索 findPdfWebViewLink の脆弱性を回避)。 401 は refreshToken で 1 回リトライ。
 * 404/403 (削除/権限外) と trashed は null を返し、 呼出側でフォルダ探索へフォールバックさせる。
 */
export async function getPdfWebViewLinkById(
  auth: DriveAuth,
  fileId: string,
): Promise<string | null> {
  const fetchImpl = auth.fetchImpl ?? fetch;
  const url = `${DRIVE_API}/files/${fileId}?fields=id,webViewLink,trashed`;
  let res = await fetchImpl(url, { headers: { Authorization: `Bearer ${auth.token}` } });
  if (res.status === 401 && auth.refreshToken) {
    const fresh = await auth.refreshToken();
    res = await fetchImpl(url, { headers: { Authorization: `Bearer ${fresh}` } });
  }
  if (!res.ok) return null; // 404/403/5xx いずれもフォールバックさせる
  const file = (await res.json()) as { id: string; webViewLink?: string; trashed?: boolean };
  if (file.trashed) return null;
  return file.webViewLink ?? `https://drive.google.com/file/d/${file.id}/view`;
}
